import { useMemo, useRef, type RefObject } from "react"

import { Button, Tooltip } from "@coval/ui"
import { useResizeObserver } from "usehooks-ts"

import { chartBarSeriesColors } from "@/components/charts/seriesPalette"

import { layoutAudioTimedValues } from "../utils/layoutAudioTimedValues"
import { formatSimulationOffset } from "../utils/simulationDataFrames"

export interface AudioTimedValue {
  startSeconds: number
  endSeconds: number
  label: string
  displayValue?: string
}

const color = chartBarSeriesColors(1)[0]
const VALUE_ROW_HEIGHT = 34
const INITIAL_TRACK_WIDTH = 640

function position(point: AudioTimedValue, durationMs: number) {
  const left = Math.max(
    0,
    Math.min(100, (point.startSeconds * 100000) / durationMs),
  )
  const end = Math.max(
    left,
    Math.min(100, (point.endSeconds * 100000) / durationMs),
  )
  return { left: `${left}%`, width: `${end - left}%`, minWidth: 3 }
}

export function AudioTimedValueOverlay({
  points,
  durationMs,
}: {
  points: AudioTimedValue[]
  durationMs: number
}) {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      {points.map((point, index) => (
        <div
          key={index}
          className="absolute inset-y-0 border-l"
          style={{
            ...position(point, durationMs),
            borderColor: color,
            backgroundColor: color,
            opacity: 0.15,
          }}
        />
      ))}
    </div>
  )
}

export function AudioTimedValues({
  points,
  durationMs,
  inset,
  onSeek,
}: {
  points: AudioTimedValue[]
  durationMs: number
  inset: { leftPx: number; rightPx: number; tailPx?: number }
  onSeek: (seconds: number) => void
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const { width = INITIAL_TRACK_WIDTH } = useResizeObserver({
    ref: trackRef as RefObject<HTMLDivElement>,
  })
  const layout = useMemo(
    () => layoutAudioTimedValues(points, durationMs, width),
    [points, durationMs, width],
  )
  const laneCount = layout.reduce(
    (count, value) => Math.max(count, value.lane + 1),
    1,
  )
  return (
    <div
      aria-label="Per-timestamp metric values"
      className="relative border-t border-coval-border py-2"
    >
      <Tooltip content="Metric values at each timestamp. Select a value to seek the recording.">
        <span
          role="button"
          tabIndex={0}
          className="absolute left-4 top-4 cursor-default select-none text-xs text-coval-text-muted"
        >
          Values
        </span>
      </Tooltip>
      <div
        data-audio-timeline-viewport
        className="max-h-36 overflow-x-hidden overflow-y-auto"
        style={{
          marginLeft: inset.leftPx,
          marginRight: inset.rightPx - (inset.tailPx ?? 0),
        }}
      >
        <div
          ref={trackRef}
          data-audio-timeline-duration={durationMs / 1000}
          className="relative"
          style={{ height: laneCount * VALUE_ROW_HEIGHT }}
        >
          {layout.map(({ point, index, labelLeft, labelWidth, lane }) => (
            <Tooltip
              key={index}
              content={`${point.label} · ${formatSimulationOffset(point.startSeconds * 1000)} – ${formatSimulationOffset(point.endSeconds * 1000)}`}
            >
              <Button
                type="button"
                variant="ghost"
                aria-label={`Value ${point.label} at ${formatSimulationOffset(point.startSeconds * 1000)}`}
                onClick={() => onSeek(point.startSeconds)}
                className="absolute h-8 rounded-sm px-0 text-xs font-medium tabular-nums text-coval-text focus-visible:z-10"
                style={{
                  left: labelLeft,
                  width: labelWidth,
                  top: lane * VALUE_ROW_HEIGHT,
                }}
              >
                {point.displayValue ?? point.label}
              </Button>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  )
}
